// Any number of the tools BossDrop connects to, A to Z, in a dropdown with a search
// box because the list is long. The Contact Sales form and the booking details
// share it. `value` is the chosen keys; with a `name`, each choice is also a hidden
// input holding its label, for a form that reads FormData.
import React, { useEffect, useMemo, useRef, useState } from "react";

const byName = (a, b) => a.label.localeCompare(b.label, "en", { sensitivity: "base" });

export default function ToolsSelect({ id, tools, value, onChange, name, placeholder = "Choose…" }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const btnRef = useRef(null);
  const searchRef = useRef(null);
  const wrapRef = useRef(null);

  const sorted = useMemo(() => [...tools].sort(byName), [tools]);

  const close = () => {
    setOpen(false);
    btnRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    // On a touch screen this would pop the keyboard over the list; tapping the box still does.
    if (!window.matchMedia?.("(pointer: coarse)")?.matches) searchRef.current?.focus({ preventScroll: true });
    // A list this long can open past the bottom of the screen.
    document.getElementById(`${id}-panel`)?.scrollIntoView?.({ block: "nearest" });

    const onDoc = (e) => {
      if (!wrapRef.current || wrapRef.current.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      btnRef.current?.focus();
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      setQuery("");
      setActiveIndex(0);
    };
  }, [open, id]);

  const term = query.trim().toLowerCase();
  const matches = sorted.filter((tool) => tool.label.toLowerCase().includes(term));
  const active = matches[activeIndex];

  // The arrow keys can walk past what the panel shows.
  useEffect(() => {
    if (open && active) {
      document.getElementById(`${id}-opt-${active.key}`)?.scrollIntoView?.({ block: "nearest" });
    }
  }, [open, active, id]);

  // A pick clears the search, ready for the next tool.
  const toggle = (key) => {
    onChange(value.includes(key) ? value.filter((k) => k !== key) : [...value, key]);
    if (term) {
      setQuery("");
      setActiveIndex(0);
    }
  };

  const onTriggerKeyDown = (e) => {
    if (!open && (e.key === "Enter" || e.key === " " || e.key === "ArrowDown")) {
      e.preventDefault();
      setOpen(true);
    }
  };

  const onSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(matches.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      // Enter here picks a tool; it must not send the form.
      e.preventDefault();
      if (active) toggle(active.key);
    } else if (e.key === "Tab") {
      setOpen(false);
    }
  };

  // While the list is open, Escape closes the list and nothing else: in the booking
  // details the same key would otherwise go back to the times.
  const onWrapKeyDown = (e) => {
    if (open && e.key === "Escape") {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };

  const names = sorted.filter((tool) => value.includes(tool.key)).map((tool) => tool.label);
  const summary =
    names.length > 3 ? `${names.slice(0, 3).join(", ")} +${names.length - 3}` : names.join(", ");

  return (
    <div className={`select ${open ? "open" : ""}`} ref={wrapRef} onKeyDown={onWrapKeyDown}>
      {name ? names.map((label) => <input type="hidden" name={name} value={label} key={label} />) : null}

      <button
        id={id}
        ref={btnRef}
        type="button"
        className="selectTrigger"
        aria-haspopup="listbox"
        aria-expanded={open ? "true" : "false"}
        aria-controls={`${id}-listbox`}
        data-placeholder={names.length ? "false" : "true"}
        title={names.join(", ") || undefined}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onTriggerKeyDown}
      >
        <span className="selectValue">{summary || placeholder}</span>
        <span className="selectCaret" aria-hidden="true" />
      </button>

      {open && (
        <div id={`${id}-panel`} className="selectPanel scrollable">
          <div className="toolSearch">
            <input
              ref={searchRef}
              type="text"
              value={query}
              placeholder="Search tools"
              aria-label="Search tools"
              aria-controls={`${id}-listbox`}
              aria-activedescendant={active ? `${id}-opt-${active.key}` : undefined}
              autoComplete="off"
              onChange={(e) => {
                setQuery(e.target.value);
                setActiveIndex(0);
              }}
              onKeyDown={onSearchKeyDown}
            />
          </div>
          <ul id={`${id}-listbox`} className="toolList" role="listbox" aria-multiselectable="true">
            {matches.map((tool, i) => {
              const selected = value.includes(tool.key);
              return (
                <li
                  id={`${id}-opt-${tool.key}`}
                  key={tool.key}
                  role="option"
                  aria-selected={selected ? "true" : "false"}
                  className={`selectOption${selected ? " selected" : ""}${i === activeIndex ? " active" : ""}`}
                  tabIndex={-1}
                  onMouseDown={(e) => e.preventDefault()} // keep focus in the search box
                  onMouseEnter={() => setActiveIndex(i)}
                  onClick={() => toggle(tool.key)}
                >
                  <span className="toolRow">
                    <img src={tool.logo} alt="" className="toolLogo" />
                    <span>{tool.label}</span>
                  </span>
                  {selected && <span className="optionCheck" aria-hidden="true" />}
                </li>
              );
            })}
          </ul>
          {matches.length ? null : <p className="toolNone">No tool by that name.</p>}
        </div>
      )}
    </div>
  );
}
